import { Clock, Smartphone } from 'lucide-react';
import { Badge } from '@/components/ui';
import { ASSISTANCE_LABELS } from '@/features/patients/types';
import type { AppCheckIn, LateNotice } from '@/features/queue/types';
import {
  allergyLine,
  klClock,
  klWhen,
  lateMinutesLabel,
  lateNoticeLabel,
} from './app-arrival-format';
import styles from './AppArrival.module.css';

const NOT_ANSWERED = 'Not answered in the app';

/**
 * RL-941 "Checked in from the app": the patient's day-before answers as the
 * API returns them (`appCheckIn`). Everything here is patient-entered and is
 * labelled so; new allergies stay "to confirm" until a doctor records them.
 */
export function AppCheckInSummary({
  checkIn,
  showEmpty = false,
  shownReason,
}: {
  checkIn: AppCheckIn | null | undefined;
  /** Say "Not checked in from the app" when there is no check-in. */
  showEmpty?: boolean;
  /** RL-1695 review: the reason already shown as "Patient says"; not repeated. */
  shownReason?: string | null;
}) {
  if (!checkIn) {
    return showEmpty ? (
      <p className={styles.sub}>Not checked in from the app.</p>
    ) : null;
  }
  const done = !!checkIn.completedAt;
  const { payer, allergies, assistance } = checkIn;
  return (
    <section className={styles.box} aria-label="App check-in">
      <div className={styles.head}>
        <Badge
          tone={done ? 'success' : 'warning'}
          icon={<Smartphone size={12} aria-hidden="true" />}
        >
          {checkIn.label}
        </Badge>
        <span className={styles.sub}>
          {done
            ? klWhen(checkIn.completedAt!)
            : `Last change ${klWhen(checkIn.updatedAt)}`}
        </span>
      </div>
      <p className={styles.source}>
        Patient-entered in the app. Confirm with the patient.
      </p>
      <dl className={styles.rows}>
        <dt>Details</dt>
        <dd>
          {checkIn.detailsConfirmedAt
            ? `Confirmed by the patient · ${klWhen(checkIn.detailsConfirmedAt)}`
            : 'Not confirmed yet'}
        </dd>
        <dt>Who pays</dt>
        <dd>
          {!payer
            ? NOT_ANSWERED
            : payer.choice === 'self'
              ? 'Self-pay'
              : payer.coverage
                ? `${payer.coverage.payerName}${payer.coverage.memberNo ? ` · ${payer.coverage.memberNo}` : ''}`
                : 'Cover (no cover details)'}
        </dd>
        {!(
          shownReason?.trim() && checkIn.reason?.trim() === shownReason.trim()
        ) && (
          <>
            <dt>Reason</dt>
            <dd>{checkIn.reason ?? NOT_ANSWERED}</dd>
          </>
        )}
        <dt>Allergies</dt>
        <dd>
          {!allergies ? (
            NOT_ANSWERED
          ) : allergies.reported.length > 0 ? (
            <>
              <ul className={styles.list}>
                {allergies.reported.map((a, i) => (
                  <li key={`${a.substance}-${i}`}>{allergyLine(a)}</li>
                ))}
              </ul>
              {allergies.toConfirm && (
                <Badge tone="warning">New allergies: doctor to confirm</Badge>
              )}
            </>
          ) : allergies.none ? (
            'No allergies'
          ) : (
            'No new allergies'
          )}
        </dd>
        <dt>Assistance</dt>
        <dd>
          {!assistance
            ? NOT_ANSWERED
            : [
                assistance.needs.length
                  ? assistance.needs
                      .map((n) => ASSISTANCE_LABELS[n]?.split(':')[0] ?? n)
                      .join(', ')
                  : 'None needed',
                assistance.note,
              ]
                .filter(Boolean)
                .join(' · ')}
        </dd>
      </dl>
    </section>
  );
}

/** RL-940 "Running late? Tell the desk": when it was sent and the delay. */
export function LateNoticeNote({
  notice,
}: {
  notice: LateNotice | null | undefined;
}) {
  if (!notice) return null;
  return (
    <p className={styles.late}>
      <Clock size={14} aria-hidden="true" />
      <span>
        Running late (told the desk from the app):{' '}
        {lateMinutesLabel(notice.minutes)} · sent {klClock(notice.sentAt)}
      </span>
    </p>
  );
}

/**
 * Compact form for a queue card or a bookings row: the late notice and the
 * check-in label as badges, and (`withAnswers`) the answers behind a
 * keyboard-reachable disclosure.
 */
export function AppArrivalBadges({
  lateNotice,
  appCheckIn,
  withAnswers = false,
}: {
  lateNotice: LateNotice | null | undefined;
  appCheckIn: AppCheckIn | null | undefined;
  withAnswers?: boolean;
}) {
  if (!lateNotice && !appCheckIn) return null;
  return (
    <>
      <div className={styles.badges}>
        {lateNotice && (
          <Badge tone="warning" icon={<Clock size={12} aria-hidden="true" />}>
            {lateNoticeLabel(lateNotice)}
          </Badge>
        )}
        {appCheckIn && (
          <Badge
            tone={appCheckIn.completedAt ? 'success' : 'warning'}
            icon={<Smartphone size={12} aria-hidden="true" />}
          >
            {appCheckIn.label}
          </Badge>
        )}
      </div>
      {withAnswers && appCheckIn && (
        <details className={styles.disclosure}>
          <summary>App answers</summary>
          <AppCheckInSummary checkIn={appCheckIn} />
        </details>
      )}
    </>
  );
}
